import { useEffect, useState } from 'react';
import type { CheckinPhotoRef } from '@awwyeah/shared';
import {
  checkinDisplayUrl,
  checkinPhotoUrl,
  checkinThumbUrl,
  isPhotoFetchable,
} from '@awwyeah/shared';
import { localPhotoBlob } from '../outbox';

/**
 * Renders a check-in's photo from whichever copy exists.
 *
 *  - **Your own photo, still queued** → the blob out of the outbox, so the person who took
 *    it sees it immediately, signal or not.
 *  - **Uploaded** → the auth'd `GET` endpoint for the requested size (immutable, so the
 *    browser caches it once).
 *  - **Someone else's, still on their phone** → the design's honest placeholder. Not a
 *    spinner and not a broken image: "photo held on this phone" says exactly what is true.
 *
 * `variant` picks which of the three server sizes to request once a photo is uploaded —
 * queued/local photos always render the local blob regardless of `variant`, since there is
 * only ever one copy of those.
 */

export type CheckinPhotoVariant = 'thumb' | 'display' | 'original';

function variantUrl(id: string, variant: CheckinPhotoVariant): string {
  if (variant === 'thumb') return checkinThumbUrl(id);
  if (variant === 'display') return checkinDisplayUrl(id);
  return checkinPhotoUrl(id);
}

export function useCheckinPhotoSource(
  ref: CheckinPhotoRef,
  variant: CheckinPhotoVariant = 'original',
): string | null {
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const fetchable = isPhotoFetchable(ref);

  useEffect(() => {
    if (fetchable) return;
    let revoked = false;
    let url: string | null = null;
    void localPhotoBlob(ref.id).then((blob) => {
      if (!blob || revoked) return;
      url = URL.createObjectURL(blob);
      setLocalUrl(url);
    });
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
      setLocalUrl(null);
    };
  }, [ref.id, fetchable]);

  if (fetchable) return variantUrl(ref.id, variant);
  return localUrl;
}

export function CheckinPhoto({
  photoRef,
  alt,
  className = '',
  variant = 'original',
  srcSetOriginal = false,
}: {
  photoRef: CheckinPhotoRef;
  alt: string;
  className?: string;
  /** Which server-generated size to request once the photo is uploaded. */
  variant?: CheckinPhotoVariant;
  /**
   * When true (and `variant` is `'display'`), adds the original as a `srcset` candidate so
   * a wide desktop viewport can pull the full-resolution image instead of the ~900px one.
   * Cheap to offer, so only offered where it might actually matter.
   */
  srcSetOriginal?: boolean;
}): JSX.Element {
  const source = useCheckinPhotoSource(photoRef, variant);
  const ratio = `${photoRef.w} / ${photoRef.h}`;

  if (!source) {
    return (
      <div
        className={`flex items-center justify-center border border-line bg-raised text-[11px] text-fg-3 ${className}`}
        style={{ aspectRatio: ratio }}
        data-testid="checkin-photo-held"
      >
        photo held on this phone
      </div>
    );
  }

  const offerSrcSet = srcSetOriginal && variant === 'display' && isPhotoFetchable(photoRef);

  return (
    <img
      src={source}
      srcSet={
        offerSrcSet
          ? `${checkinDisplayUrl(photoRef.id)} 900w, ${checkinPhotoUrl(photoRef.id)} ${photoRef.w}w`
          : undefined
      }
      sizes={offerSrcSet ? '(min-width: 900px) 900px, 100vw' : undefined}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={`w-full border border-line bg-raised object-cover ${className}`}
      style={{ aspectRatio: ratio }}
      data-testid="checkin-photo"
    />
  );
}
